import {
  Icon,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Paper,
  Search
} from '@sanlam-glacier/app-common/components';
import { useCallback, useRef, useState } from 'react';
import styles from './search-bar.module.scss';
import { debounce } from 'lodash';
import { useInputsDisabled } from 'providers/disabled-provider/DisabledFieldsProvider';
import {
  blockIllegalCharacters,
  sanitizeIllegalCharacters
} from 'utils/string-handling/block-illegal-characters';
import { PlaceResultDetails } from 'modules/new-business/components/organisms/address-details-form/address-details-function';

type SearchBarProps<T> = {
  searchResults?: T[];
  searchClassName?: string;
  searchTitle: string;
  isLoading?: boolean;
  resultTitle?: string;
  helperText?: string;
  handleItemSelect?: (item: T) => void;
  renderSearchResultRow?: (row: T) => JSX.Element | null;
  handleSearch?: (value?: string, isFocused?: boolean) => void;
  size?: 'medium' | 'small';
  width?: number;
  googleAPIKey?: string;
  noResultsMessage?: string;
  onPlaceSelect?: (placeDetails: PlaceResultDetails) => void;
  debounceDelay?: number;
  getValueToDisplayOnceSelected?: () => string | undefined;
  defaultValue?: string;
  maxSearchLength?: number;
};

export const SearchBar = <T,>({
  searchResults,
  searchClassName,
  searchTitle,
  isLoading,
  helperText,
  resultTitle = 'result',
  handleItemSelect,
  handleSearch,
  renderSearchResultRow,
  size,
  width,
  googleAPIKey,
  noResultsMessage,
  onPlaceSelect,
  debounceDelay,
  getValueToDisplayOnceSelected,
  defaultValue,
  maxSearchLength
}: SearchBarProps<T>) => {
  const inputsDisabled = useInputsDisabled();
  const [search, setSearch] = useState<string>(defaultValue ?? '');
  const [isSearchResultOpen, setIsSearchResultOpen] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const debouncedHandleSearch = useCallback(
    debounce(async (value?: string, isFocused?: boolean) => {
      if (!handleSearch) return;
      handleSearch(value, isFocused);

      if (value === '') {
        setIsSearchResultOpen(false);
        setSearch('');
        return;
      }
      const currentSearchTerm = value ?? (isFocused ? search : '');
      const trimmedSearch = currentSearchTerm.trim().replace(' ', '');
      setSearch(value ?? '');
      if ((isFocused && trimmedSearch.length >= 3) || trimmedSearch.length >= 3) {
        setIsSearchResultOpen(true);
      } else {
        setIsSearchResultOpen(false);
      }
    }, debounceDelay ?? 600),
    []
  );

  const handleSearchClick = () => {
    if (search !== '') {
      setIsSearchResultOpen(true);
    }
  };

  const onSelectSearchResult = (result: T) => {
    if (handleItemSelect) handleItemSelect(result);
    setIsSearchResultOpen(false);

    if (getValueToDisplayOnceSelected) setSearch(getValueToDisplayOnceSelected() ?? '');
    else setSearch('');
  };

  const searchResultsCount = searchResults?.length ?? 0;

  const openSearchMenu = isSearchResultOpen && search !== '';

  const searchBarSize = size === 'medium' ? Search.SIZE.MEDIUM : Search.SIZE.SMALL;

  const handleKeyPress = (event: KeyboardEvent) => {
    if (blockIllegalCharacters(event.key)) {
      event.preventDefault();
    }
  };
  return (
    <>
      <div ref={ref}>
        <Search
          controlled
          value={search}
          inputProps={{ onKeyPress: handleKeyPress }}
          onChange={(value) => {
            const cleanValue = maxSearchLength
              ? sanitizeIllegalCharacters(value.slice(0, maxSearchLength))
              : sanitizeIllegalCharacters(value);
            if (cleanValue === '' || !cleanValue) {
              debouncedHandleSearch.cancel();

              if (handleSearch) handleSearch('');

              setSearch('');
              return;
            }
            debouncedHandleSearch.cancel();
            debouncedHandleSearch(cleanValue);
          }}
          onClick={() => handleSearchClick()}
          className={searchClassName}
          loading={isLoading}
          placeholder={searchTitle}
          assistiveText={openSearchMenu ? undefined : helperText}
          size={searchBarSize}
          disabled={inputsDisabled}
          googleAPIKey={googleAPIKey}
          noResultsMessage={noResultsMessage}
          onPlaceSelect={onPlaceSelect}
        />
      </div>

      {searchResults && (
        <Menu
          maxHeight={290}
          aria-label='ClientMenu'
          anchorEl={ref.current !== null ? ref.current : undefined}
          position={Menu.POSITION.BOTTOM_START}
          fullWidth={true}
          minWidth={width}
          open={openSearchMenu}
          preventFocusOnOpen={true}
          onClose={() => {
            setIsSearchResultOpen(false);
            ref.current?.blur();
          }}
        >
          <div className={styles.searchHeadingText}>
            <span>
              <Typography
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.LIGHT}
                color={Typography.COLOR.TERTIARY}
              >
                Search results
              </Typography>
            </span>
            <span className={styles.searchResultsCount}>
              <Typography variant={Typography.VARIANT.B4} weight={Typography.WEIGHT.MEDIUM}>
                {`${searchResultsCount} ${
                  searchResultsCount > 1 || searchResultsCount == 0
                    ? `${resultTitle}s`
                    : resultTitle
                } found`}
              </Typography>
            </span>
          </div>

          {searchResultsCount > 0 ? (
            <Paper>
              <Divider />
              {searchResults.map((value: T, index: number) => (
                <MenuItem
                  key={index}
                  onClick={() => onSelectSearchResult(value)}
                  onFocus={(e) => {
                    e.target.blur();
                  }}
                >
                  {renderSearchResultRow!(value)}
                </MenuItem>
              ))}
            </Paper>
          ) : (
            <Paper>
              <Divider />
              <div className={styles.emptySearchStepOne}>
                <div className={styles.noSearchResultsIcon}>
                  <Icon icon='action_youtube-searched-for' />
                </div>
                <Typography
                  variant={Typography.VARIANT.B2}
                  weight={Typography.WEIGHT.NORMAL}
                  className={styles.noSearchResultsSpacing}
                >
                  <p>We are unable to find a result for</p>
                  <div className={styles.noSearchResultsSearchTerm}>
                    <Typography
                      variant={Typography.VARIANT.B2}
                      weight={Typography.WEIGHT.BOLD}
                      color={Typography.COLOR.DEFAULT}
                    >
                      {`"${search}"`}
                    </Typography>
                  </div>
                </Typography>
                <Typography
                  variant={Typography.VARIANT.B2}
                  weight={Typography.WEIGHT.NORMAL}
                  className='spacing'
                >
                  Please try another search.
                </Typography>
              </div>
            </Paper>
          )}
        </Menu>
      )}
    </>
  );
};
